import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    ColumnDatatable,
    FilterDatatable,
    FilterOptionValue,
    FiltersDatatableList,
    StoreDatatable,
} from '@europrocurement/flexy-datatable';
import { useDispatch, useSelector } from 'react-redux';
import {
    CATALOGS_FULL_SLICE_NAME,
    CatalogFullSelector,
    useOffreService,
    useSociete,
} from '@europrocurement/l2d-domain';
import { DataSource } from '@europrocurement/l2d-redux-utils';
import {
    CatalogueJsonldCatalogueReadCatalogueReadGrilleTarifaireCatalogueReadFull,
    TblTypeDocumentJsonldCatalogueReadCatalogueReadGrilleTarifaireCatalogueReadFull,
} from '@europrocurement/l2d-domain/openApi/ApiOffre';
import { Box, CircularProgress, Typography } from '@mui/material';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { AnyAction } from '@reduxjs/toolkit';
import { useOfferContext } from '@europrocurement/l2d-modules/modules/Offres/providers/OfferContext';
import {
    InformationCard,
    InformationCardItemProps,
    useModal,
} from '@europrocurement/flexy-components';
import ActionMenuGrilleTarifaire from '../components/ActionMenuGrilleTarifaire';
import useFilterOptions from '../../Offres/Catalog/useFilterOptions';
import { OffreFormalite, useOffreFormalite } from '../hook/useOffreFormalite';
import useOffrePrescripteur, { Tiers } from '../hook/useOffrePrescripteur';
import TarifCell from '../components/DataTableCell/TarifCell';
import { useOrigine } from '../../ParcoursFormalite/hooks';
import useOfferCache from '../../Offres/cache/useOfferCache';
import { PossibleTypes, useSearchBar } from '../../Societes';

function calculByType(
    type: string,
    row: CatalogueJsonldCatalogueReadCatalogueReadGrilleTarifaireCatalogueReadFull,
): number | string {
    switch (type) {
        case 'ht':
            return (row.tarifHT ?? 0.0).toFixed(2);
        case 'ttc':
            return row.tarifTTC ?? '0.00';
        default:
            return '0.00';
    }
}

export const GrilleTarifaire: React.FunctionComponent<{
    selectedPrescriberId?: number;
    showOrigineFilter?: boolean;
    showTTC: boolean;
    showNumberCatalogue: boolean;
    showOffer: boolean;
    showElement: boolean;
    showAction: boolean;
    showChips?: boolean;
    surchargeHonoraire: boolean;
    surchargeCatalogue?: boolean;
    surchargePackage?: boolean;
    surchargeFD: boolean;
    surchargeAL: boolean;
    waitPrescripteur: boolean;
    showDate?: boolean;
    showFilter?: boolean;
}> = function ({
    selectedPrescriberId,
    showOrigineFilter = false,
    showTTC = true,
    showNumberCatalogue = false,
    showOffer = false,
    showElement = false,
    showAction = false,
    showChips = true,
    surchargeHonoraire = false,
    surchargeCatalogue = false,
    surchargePackage = false,
    surchargeFD = false,
    surchargeAL = false,
    waitPrescripteur = false,
    showDate = false,
    showFilter = true,
}) {
    const xIdSociete = useSociete();
    const datasource: DataSource<CatalogueJsonldCatalogueReadCatalogueReadGrilleTarifaireCatalogueReadFull> =
        useSelector(CatalogFullSelector).main;
    const { getCatalogsFull } = useOffreService();
    const filterOptions = useFilterOptions({ groups: { field: 'label' } });
    const { searchOfferFormalite } = useOffreFormalite();
    const { getPrescriber } = useOffrePrescripteur();
    const [editPrescripteur, setEditPrescripteur] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(waitPrescripteur);
    const origineId = useOrigine();
    const hasDispatchedOnMount = useRef(false);
    const { setIsSearchBarPath, setSearchBarType } = useSearchBar();
    const { offerDataLoaded, getTiersCurrentOfferWithDefault } = useOfferContext();
    const [idPrescriber, setIdPrescriber] = useState<number | undefined>(undefined);
    const { modalActions } = useModal();

    const dispatch = useDispatch();
    const refreshData = useCallback(
        () => dispatch(getCatalogsFull({}) as unknown as AnyAction),
        [dispatch, getCatalogsFull],
    );

    // Activation du SearchBar pour les grilles tarifaires
    React.useEffect(() => {
        setSearchBarType(PossibleTypes.PricesGrid);
        setIsSearchBarPath(true);
        return () => {
            setSearchBarType(undefined);
            setIsSearchBarPath(false);
        };
    }, [setIsSearchBarPath, setSearchBarType]);

    useEffect(
        () => () => {
            dispatch({
                type: `${CATALOGS_FULL_SLICE_NAME}/resetmainFilter`,
            });
            hasDispatchedOnMount.current = false;
        },
        // doit se jouer au démontage du composant
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );
    useEffect(() => {
        if (!selectedPrescriberId) {
            return;
        }
        dispatch({
            type: `${CATALOGS_FULL_SLICE_NAME}/setmainFilter`,
            payload: {
                key: 'surchargePrescripteurPrescripteur',
                value: selectedPrescriberId,
            },
        });
        dispatch({
            type: `${CATALOGS_FULL_SLICE_NAME}/setmainFilter`,
            payload: {
                key: 'origineId',
                value: origineId,
            },
        });
        setIdPrescriber(selectedPrescriberId);
        setLoading(false);
    }, [selectedPrescriberId, origineId, dispatch]);

    useEffect(() => {
        if (datasource.filters.surchargePrescripteurPrescripteur && !selectedPrescriberId) {
            setIdPrescriber(Number(datasource.filters.surchargePrescripteurPrescripteur));
        } else if (!selectedPrescriberId) {
            setIdPrescriber(undefined);
        }
    }, [datasource.filters.surchargePrescripteurPrescripteur, selectedPrescriberId]);

    useEffect(() => {
        if (!hasDispatchedOnMount.current) {
            const today = new Date().toISOString().split('T')[0];
            dispatch({
                type: `${CATALOGS_FULL_SLICE_NAME}/setmainFilter`,
                payload: {
                    key: 'dateDebutBefore',
                    value: today,
                },
            });
            dispatch({
                type: `${CATALOGS_FULL_SLICE_NAME}/setmainFilter`,
                payload: {
                    key: 'dateFinAfter',
                    value: today,
                },
            });
            hasDispatchedOnMount.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // à se jouer qu'au montage du composant

    useEffect(() => {
        if (!selectedPrescriberId || !offerDataLoaded) {
            return;
        }

        (async () => {
            const offer = await getTiersCurrentOfferWithDefault(selectedPrescriberId);
            dispatch({
                type: `${CATALOGS_FULL_SLICE_NAME}/setmainFilter`,
                payload: {
                    key: 'offreId',
                    value: offer?.id,
                },
            });
            refreshData();
        })();
    }, [
        selectedPrescriberId,
        getTiersCurrentOfferWithDefault,
        dispatch,
        refreshData,
        offerDataLoaded,
    ]);

    useEffect(() => {
        if (hasDispatchedOnMount.current !== undefined || offerDataLoaded) {
            refreshData();
        }
    }, [hasDispatchedOnMount, offerDataLoaded, refreshData]);
    const dataColumnsRender: ColumnDatatable<CatalogueJsonldCatalogueReadCatalogueReadGrilleTarifaireCatalogueReadFull>[] =
        useMemo(() => {
            const isEditPrescripteur: boolean = !!(
                surchargeHonoraire ||
                (datasource.filters.surchargePrescripteurPrescripteur && !selectedPrescriberId)
            );
            const handleOpenDocModal = (
                documents: TblTypeDocumentJsonldCatalogueReadCatalogueReadGrilleTarifaireCatalogueReadFull[],
            ) => {
                const itemsDocument: InformationCardItemProps[] = [];
                documents.map(
                    (
                        doc: TblTypeDocumentJsonldCatalogueReadCatalogueReadGrilleTarifaireCatalogueReadFull,
                    ) =>
                        itemsDocument.push({
                            label: doc.libelle || '',
                            value: doc.help,
                        }),
                );

                modalActions.call(
                    <Box>
                        <InformationCard
                            title="Documents Requis"
                            items={itemsDocument}
                            subtitle=""
                        />
                    </Box>,
                );
            };
            setEditPrescripteur(isEditPrescripteur);
            const dataColumns: ColumnDatatable<CatalogueJsonldCatalogueReadCatalogueReadGrilleTarifaireCatalogueReadFull>[] =
                [];
            if (showNumberCatalogue) {
                dataColumns.push({
                    isDisplayed: true,
                    sortable: true,
                    label: 'N°',
                    field: 'id',
                    render: ({ id }) => (
                        <Typography
                            variant="l2dtableCell"
                            color="text.gris70"
                        >
                            {id}
                        </Typography>
                    ),
                    sx: {
                        fontWeight: 500,
                        paddingLeft: '40px',
                    },
                });
            }
            if (showOffer) {
                dataColumns.push({
                    isDisplayed: true,
                    sortable: true,
                    label: 'Offres',
                    field: 'offreLibelle',
                    render: ({ offre }) => (
                        <Typography
                            variant="l2dtableCell"
                            color="text.gris70"
                        >
                            <FaOptionIcon icon={['fad', offre?.iconeName as IconName]} />
                        </Typography>
                    ),
                });
            }

            dataColumns.push(
                {
                    isDisplayed: true,
                    label: 'Formalités',
                    sortable: true,
                    field: 'groupementLibelle',
                    sx: showNumberCatalogue || showOffer ? {} : { minWidth: '35%' },
                    render: ({ groupement, nomCommercial }) => (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <Typography
                                variant="l2dtableCell"
                                color="text.gris70"
                            >
                                {groupement?.libelle}
                            </Typography>
                            <Typography
                                variant="l2dtableCell"
                                color="text.gris50"
                            >
                                {nomCommercial}
                            </Typography>
                        </Box>
                    ),
                },
                {
                    isDisplayed: false,
                    label: 'Date début',
                    sortable: true,
                    field: 'dateDebut',
                    render: ({ dateDebut }) => (
                        <Typography
                            variant="l2dtableCell"
                            color="text.gris70"
                        >
                            {dateDebut}
                        </Typography>
                    ),
                },
                {
                    isDisplayed: false,
                    label: 'Date fin',
                    sortable: true,
                    field: 'dateFin',
                    render: ({ dateFin }) => (
                        <Typography
                            variant="l2dtableCell"
                            color="text.gris70"
                        >
                            {dateFin}
                        </Typography>
                    ),
                },
                {
                    isDisplayed: true,
                    sortable: false,
                    label: 'Honoraires',
                    field: undefined,
                    render: (row) => (
                        <Box>
                            <TarifCell
                                type="honoraire"
                                row={row}
                                editable={isEditPrescripteur}
                                surchargeCatalogue={surchargeCatalogue}
                                surchargePackage={surchargePackage}
                                idPrescriber={idPrescriber}
                                showChips={showChips}
                            />
                        </Box>
                    ),
                },
                {
                    isDisplayed: true,
                    sortable: false,
                    label: 'Frais de dossier',
                    field: undefined,
                    render: (row) => (
                        <TarifCell
                            type="frais"
                            row={row}
                            editable={surchargeFD}
                            surchargeCatalogue={surchargeCatalogue}
                            surchargePackage={surchargePackage}
                            showChips={showChips}
                        />
                    ),
                },
                {
                    isDisplayed: true,
                    sortable: false,
                    label: 'Annonces légales',
                    field: undefined,
                    render: (row) => (
                        <TarifCell
                            type="annonce"
                            row={row}
                            editable={surchargeAL}
                            surchargeCatalogue={surchargeCatalogue}
                            surchargePackage={surchargePackage}
                            showChips={showChips}
                        />
                    ),
                },
            );
            if (showElement) {
                dataColumns.push({
                    isDisplayed: true,
                    sortable: false,
                    label: 'Elements',
                    field: undefined,
                    render: (row) => {
                        const formaliteDocs = row.package?.formalite?.documents?.length ?? 0;
                        const totalDocs = formaliteDocs;
                        return (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    gap: '8px',
                                    color: 'text.gris70',
                                }}
                            >
                                <Typography
                                    variant="l2dtableCell"
                                    color="text.gris70"
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        gap: '1px',
                                        cursor: totalDocs ? 'pointer' : 'inherit',
                                    }}
                                    onClick={() =>
                                        totalDocs > 0 &&
                                        row.package &&
                                        row.package.formalite &&
                                        row.package.formalite.documents
                                            ? handleOpenDocModal(row.package.formalite.documents)
                                            : null
                                    }
                                >
                                    <FaOptionIcon icon={['fad', 'paperclip']} />
                                    {totalDocs}
                                </Typography>
                                <Typography
                                    variant="l2dtableCell"
                                    color="text.gris70"
                                    sx={{ display: 'flex', flexDirection: 'row', gap: '1px' }}
                                >
                                    <FaOptionIcon icon={['fad', 'money-check-pen']} />
                                    {row.options?.length}
                                </Typography>
                            </Box>
                        );
                    },
                });
            }
            dataColumns.push({
                isDisplayed: true,
                sortable: false,
                label: 'Total HT',
                field: undefined,
                render: (row) => (
                    <Box
                        display="flex"
                        flexDirection="row"
                        gap="2px"
                        color="text.primary"
                    >
                        <Typography
                            variant="l2dtableCell"
                            color="text.gris70"
                        >
                            {calculByType('ht', row)}
                        </Typography>
                        <Typography
                            variant="l2dtableCell"
                            color="text.gris70"
                        >
                            €
                        </Typography>
                    </Box>
                ),
            });
            if (showTTC) {
                dataColumns.push({
                    isDisplayed: true,
                    sortable: false,
                    label: 'Total TTC',
                    field: undefined,
                    render: (row) => (
                        <Box
                            display="flex"
                            flexDirection="row"
                            gap="2px"
                            sx={{ color: 'text.primary' }}
                        >
                            <Typography
                                variant="l2dtableCell"
                                color="text.gris70"
                            >
                                {calculByType('ttc', row)}
                            </Typography>
                            <Typography
                                variant="l2dtableCell"
                                color="text.gris70"
                            >
                                €
                            </Typography>
                        </Box>
                    ),
                });
            }
            if (showAction) {
                dataColumns.push({
                    label: 'Actions',
                    displayLabel: true,
                    render: (row) =>
                        row.id && (
                            <ActionMenuGrilleTarifaire
                                showPackage={surchargePackage}
                                showCatalogue={surchargeCatalogue}
                                showPrescripteur={editPrescripteur}
                                catalogue={row}
                                idPrescripteur={idPrescriber}
                            />
                        ),
                    isDisplayed: true,
                });
            }

            return dataColumns;
        }, [
            surchargeHonoraire,
            surchargePackage,
            surchargeCatalogue,
            datasource.filters.surchargePrescripteurPrescripteur,
            selectedPrescriberId,
            showNumberCatalogue,
            showOffer,
            showElement,
            showTTC,
            showAction,
            showChips,
            idPrescriber,
            surchargeFD,
            surchargeAL,
            editPrescripteur,
            modalActions,
        ]);

    useOfferCache();
    /** Generic function : Generate filter object */
    const getFilter = (
        label: string,
        field: string,
        options: FilterOptionValue[],
    ): FilterDatatable => ({
        label,
        field,
        type: 'staticSelect' as const,
        options,
        renderFilterList: (value: string) => {
            const selectedOption = options.find((option) => option.value === value);
            return selectedOption ? selectedOption.label : 'Aucun';
        },
    });

    const catalogsFilters: FiltersDatatableList = [
        ...(!selectedPrescriberId
            ? [
                  getFilter('Domaine', 'domaineId', filterOptions.domains),
                  {
                      label: 'Prescripteur',
                      field: 'surchargePrescripteurPrescripteur',
                      type: 'selectApi',
                      searchFunction: getPrescriber,
                      renderItem: (item: Tiers) => item.raisonSociale,
                      valueMap: (item: Tiers) => item.id as unknown as string,
                  } as FilterDatatable,
              ]
            : []),
        ...(showOffer ? [getFilter('Offre', 'offreId', filterOptions.offers)] : []),
        ...(showOrigineFilter ? [getFilter('Origine', 'origineId', filterOptions.origins)] : []),
        getFilter('Regroupement', 'groupementId', filterOptions.groups),
        {
            label: 'Formalités',
            field: 'packageFormaliteId',
            type: 'selectApi',
            searchFunction: searchOfferFormalite,
            renderItem: (item: OffreFormalite) => item.libelle as unknown as string,
            valueMap: (item: OffreFormalite) => item.id as unknown as string,
        },
        ...(showDate
            ? [
                  {
                      label: 'Date début',
                      field: 'dateDebutBefore',
                      type: 'date' as const,
                  },
                  {
                      label: 'Date de fin',
                      field: 'dateFinAfter',
                      type: 'date' as const,
                  },
              ]
            : []),
    ];

    if (loading && waitPrescripteur) {
        return <CircularProgress sx={{ height: '500px' }} />;
    }

    return (
        <StoreDatatable
            dataSource={datasource}
            columns={dataColumnsRender}
            fetchData={getCatalogsFull}
            filters={catalogsFilters}
            filtersControl={showFilter}
            localStorageKey="FlexyStoreDatatableGrilleTarifaire"
            observers={[xIdSociete]}
            sx={{ marginBottom: '2.25em' }}
        />
    );
};

export default GrilleTarifaire;
